import { Button } from "primereact/button";
import { toastService } from "../../services/toastService";
import React, { Component } from "react";
import Loader from "../../components/Loader";
import { MerchantAddresses } from "../../models/MerchantModel";
import { SelectItem } from "../../models/SelectItem";
import { merchantAddressApiService } from "../../services/api/merchants/MerchantAddressApiService";
import AddMerchantAddress from "./AddMerchantAddress";
import { confirmDanger } from "../../utils/confirmHelpers";
import { isMerchantProfileUpdateAllowed } from "../../authGuard";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { utils } from "../../utils/utils";

interface MerchantAddressProps {
    merchantId: number;
    localMode: boolean;
    onLocalAddressesUpdated?: (contact: MerchantAddresses[]) => void;
    merchantAddresses?: MerchantAddresses[];
}

interface MerchantAddressState {
    address: MerchantAddresses[];
    isLoading: boolean;
    isAddressDetailVisible: boolean;
    selectedAddress: MerchantAddresses | null;
}

export default class MerchantAddress extends Component<MerchantAddressProps, MerchantAddressState> {
    addressTypes: SelectItem[] = [];
    countries: SelectItem[] = [];
    constructor(props: MerchantAddressProps | Readonly<MerchantAddressProps>) {
        super(props);

        this.state = {
            address: this.props.localMode ? this.props.merchantAddresses! : [],
            isLoading: true,
            isAddressDetailVisible: false,
            selectedAddress: null,
        };
    }

    async componentDidMount() {
        const countriesResult = await utils.getCountriesResult();

        if (!countriesResult.success) {
            toastService.error(countriesResult.message);
            this.setState({ isLoading: false });
            return;
        }

        this.countries = utils.getCountries();

        const addressTypes = await merchantAddressApiService.getAddressTypes();

        if (!addressTypes.success) {
            toastService.error(addressTypes.message);
            this.setState({ isLoading: false });

            return;
        }

        this.setState({ isLoading: false });
        this.addressTypes = addressTypes.data;

        await this.loadAddress();
    }

    loadAddress = async () => {
        if (this.props.localMode) {
            return;
        }

        this.setState({ isLoading: true });

        const address = await merchantAddressApiService.getAll(this.props.merchantId);

        this.setState({ isLoading: false });

        if (!address.success) {
            toastService.error(address.message);
            return;
        }

        this.setState({ address: address.data });
    };

    getEmptyAddress = (): MerchantAddresses => {
        return {
            id: 0,
            addressLine: "",
            city: "",
            zipCode: "",
            countryId: "",
            addressType: "",
            stateProvinceCounty: "",
            merchantId: this.props.merchantId,
        };
    };

    addorEditAddress = (rowData: MerchantAddresses) => {
        this.setState({ isAddressDetailVisible: true, selectedAddress: rowData });
    };

    closeAddressModel = () => {
        this.setState({ isAddressDetailVisible: false });
    };

    onDetailsModalClose = async (address: MerchantAddresses) => {
        this.closeAddressModel();
        if (this.props.localMode && address) {
            const addresses = this.state.address;
            if (addresses.length < this.addressTypes.length) {
                if (this.addressTypes.find((i) => i.value == address.addressType)) {
                    if (addresses.find((i) => i.addressType === address.addressType)) {
                        toastService.error("Address Type already exists");
                    } else {
                        addresses.push(address!);
                        this.setState({ address: addresses });
                        this.props.onLocalAddressesUpdated!(this.state.address);
                    }
                } else {
                    toastService.error("Address type is not valid.");
                }
            } else {
                toastService.error("Address limit exceeds.");
            }
        }
        await this.loadAddress();
    };

    confirmDeleteAddress = async (address: MerchantAddresses) => {
        if (this.props.localMode) {
            const addresses = this.state.address;
            const index = addresses.findIndex(
                (f) =>
                    f.addressLine == address.addressLine &&
                    f.addressType == address.addressType &&
                    f.city == address.city &&
                    f.countryId == address.countryId &&
                    f.zipCode == address.zipCode,
            );

            if (index >= 0) {
                addresses.splice(index, 1);
            }

            this.setState({ address: addresses });

            this.props.onLocalAddressesUpdated!(this.state.address);
            return;
        }

        confirmDanger(
            "This action will remove the address and is irreversible. Are you sure you want to proceed?",
            "Delete Address",
            () => this.deleteAddress(address),
        );
    };

    deleteAddress = async (rowData: MerchantAddresses) => {
        const deleteResult = await merchantAddressApiService.delete(rowData.id);

        if (deleteResult.success) {
            this.setState({ address: this.state.address.filter((address) => address.id !== rowData.id) });
        } else {
            toastService.error(deleteResult.message);
        }
    };

    getAddressTypeLabel = (type: string) => {
        return this.addressTypes.find((t) => t.value === type)?.label ?? type;
    };

    getCountryName = (countryId: string) => {
        return utils.getCountryName(countryId) ?? countryId;
    };

    formatAddressLine = (addr: MerchantAddresses) => {
        const parts = [addr.addressLine, addr.city, addr.stateProvinceCounty, addr.zipCode].filter(Boolean);
        return parts.join(", ");
    };

    renderAddressCard = (addr: MerchantAddresses, index: number) => {
        const typeLabel = this.getAddressTypeLabel(addr.addressType);
        const countryName = this.getCountryName(addr.countryId);
        const addressLine = this.formatAddressLine(addr);
        const canEdit = !this.props.localMode && isMerchantProfileUpdateAllowed();

        return (
            <div
                key={addr.id || index}
                className={`flex items-start gap-3 px-4 py-3.5 ${index > 0 ? "border-t border-neutral-100 dark:border-neutral-800" : ""}`}
            >
                {/* Icon */}
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary-50 text-primary-500 dark:bg-primary-900/20 dark:text-primary-400 shrink-0 mt-0.5">
                    <MapPin size={18} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                            {typeLabel}
                        </span>
                    </div>
                    {addressLine && (
                        <div className="text-sm text-neutral-900 dark:text-neutral-100">{addressLine}</div>
                    )}
                    {countryName && (
                        <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{countryName}</div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                    {canEdit && (
                        <button
                            type="button"
                            className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                            onClick={() => this.addorEditAddress(addr)}
                            title="Edit address"
                        >
                            <Pencil size={15} className="text-neutral-400 hover:text-primary-500 transition-colors" />
                        </button>
                    )}
                    {isMerchantProfileUpdateAllowed() && (
                        <button
                            type="button"
                            className="p-1.5 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                            onClick={() => this.confirmDeleteAddress(addr)}
                            title="Delete address"
                        >
                            <Trash2 size={15} className="text-neutral-400 hover:text-danger-500 transition-colors" />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    render() {
        return (
            <div>
                {this.state.isAddressDetailVisible && this.state.selectedAddress != null && (
                    <AddMerchantAddress
                        onHide={this.closeAddressModel}
                        merchantId={this.props.merchantId}
                        address={this.state.selectedAddress}
                        countries={this.countries}
                        types={this.addressTypes}
                        localMode={this.props.localMode}
                        onSubmit={(address) => this.onDetailsModalClose(address)}
                    />
                )}

                {/* Header with Add button */}
                <div className="flex items-center justify-between px-4 py-3">
                    <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Addresses</h3>
                    {isMerchantProfileUpdateAllowed() && (
                        <Button
                            label="Add Address"
                            outlined
                            size="small"
                            icon={<Plus size={16} />}
                            onClick={() => this.addorEditAddress(this.getEmptyAddress())}
                        />
                    )}
                </div>

                {/* Address list or loading/empty state */}
                {this.state.isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader />
                    </div>
                ) : this.state.address.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-neutral-400 dark:text-neutral-500">
                        <MapPin size={36} strokeWidth={1.5} />
                        <p className="mt-2 text-sm">No addresses added yet.</p>
                    </div>
                ) : (
                    <div>
                        {this.state.address.map((addr, idx) => this.renderAddressCard(addr, idx))}
                    </div>
                )}
            </div>
        );
    }
}
